/* @flow */
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { TApartData } from '../../types/apart';
import { CalcIcon, houseIconSmall, isNewApartIcon } from '../Icons';
import { Tooltip } from '../Tooltip/Tooltip';
import { LIRA_SET_LIVING_BANNER_TEXTS } from '../../constants';

import emptyLogo from '../../../public/images/errors/empty-developer-logo.png';
import bannerIcon from '../../../public/images/apart/living-banner.png';
import bannerIcon2x from '../../../public/images/apart/living-banner@2x.png';
import bannerIcon3x from '../../../public/images/apart/living-banner@3x.png';

import {
	ButtonWrapper,
	CalcImg,
	DeveloperLogo,
	DislikePopupWrapper,
	HelpText,
	InfoArea,
	LivingBanner,
	LivingBannerButton,
	LivingBannerIcon,
	LivingBannerText,
	LivingBannerTitle,
	Price,
	PriceInfo,
	PriceTitle,
	PriceTotalWrapper,
	PriceValue,
	RightBlock,
	SellerInfo,
	SidebarMortgageInfo,
	SidebarMortgageInfoProgram,
	SidebarMortgageLink,
	SidebarNormalPrice,
	SidebarPricePerMetr,
	StatusLabels,
	StatusWrapper,
	StickyBarRoot,
	StickyBarWrapper,
	StickyBlock,
	StockLabel,
	StockName,
	StockNameWrapper,
	StockRow,
	UpdatedText,
	UpdatedTime,
} from './styles';
import type { TLiraSelectionsState } from '../../reducers/liraSelections';
import { getTextsFromDatabase } from '../../actions/lira';
import type { TDispatch } from '../../types/core';
import { AppContext } from '../AppContext';
import PopupSelector from '../LikeCard/PopupSelector';
import type { TRootState } from '../../types/rootState';
import type { TApartment } from '../../reducers/apartment';
import { formatValuesNumber } from '../../utils/formatting';
import type { TFilterGlobal } from '../../types/filters';
import { checkIsResaleById } from '../../utils';
import Label from '../Label';
import theme from '../../theme';
import RankPlaceRelevance from '../ApartPage/Components/RankPlaceRelevance';
import LikeDislikeBtns from '../ApartPage/Components/LikeDislikeBtns';
import LikeDislikeMark from '../LikeDislikeMark';
import SoldMark from '../SoldMark';
import CompareShareBtns from '../CompareShareBtns';
import PriceDiffHistory from '../PriceDiffHistory';
import Button from '../Button2.0';
import type { TMortgage } from '../../types/mortgage';
import { changeApartOpinionCommon } from '../../utils/changeApartOpinion';
import { setDislikeMenuOpen } from '../../actions/apartment';
import ApartCardTags from '../ApartCardTags';
import { useApartStatus } from '../../utils/hooks';

type TProps = {
	isResale: boolean,
	handleScrollToMortgage: () => {},
	handlerModalToggle: () => {},
	handleScrollToDiscounts?: () => {},
};

const StickyBar = ({
	handleScrollToDiscounts,
	handleScrollToMortgage,
	handlerModalToggle,
	isResale = false,
}: TProps) => {
	const dispatch: TDispatch = useDispatch();
	const store = useStore();
	const context = useContext(AppContext);

	const apartment: TApartment = useSelector((state: TRootState) => state.apartment);
	const filterGlobal: TFilterGlobal = useSelector((state: TRootState) => state.filterGlobal);
	const liraSelections: TLiraSelectionsState = useSelector((state: TRootState) => state.liraSelections);
	const mortgage: TMortgage = useSelector((state: TRootState) => state.mortgage);
	const isMobile: boolean = useSelector((state: TRootState) => state.app.isMobile);

	const { livingBanner, dislikeMenuOpen } = apartment;
	const apartData = isResale ? apartment.apartDataResale : apartment.apartData;

	const [dislikeOpen, setDislikeOpen] = useState(dislikeMenuOpen);
	const { typeMark, isOpenTypeMark } = useApartStatus(apartData.id);

	const { data } = liraSelections;

	const {
		id,
		complex,
		priceTotalMin,
		priceDiscount,
		discountSum,
		pricePerMeter,
		isActive,
		lastUpdateAt,
		nmarketId,
		sellerType,
		isPresent,
		building,
		mortgageInfo,
		isApartHotel,
		tags,
	}: TApartData = apartData;

	const developer = complex ? complex.developer : null;
	const discounts = building ? building.discounts : {};

	const FIRST_DISCOUNT_NAME = discounts[0]?.name;

	const userDislikes = useMemo(() => data?.disliked?.filter((item) => item.id === id)[0]?.notices);

	const getSellerTypeText = useCallback(() => {
		switch (sellerType) {
			case 'Переуступка':
				return '(П)';
			case 'Застройщик':
				return '(З)';
			default:
				return null;
		}
	}, [sellerType]);

	const getFormattedDate = useCallback((lag, date) => {
		const options = {
			day: 'numeric',
			month: 'long',
		};
		const oneDay = 24 * 60 * 60 * 1000;

		if (lag <= 0) return 'сегодня';
		if (lag > 0 && lag <= oneDay) return 'вчера';
		return date.toLocaleString('ru', options);
	}, []);

	const getUpdateDate = useCallback(() => {
		const now = new Date();
		const today = new Date(now.setHours(0, 0, 0, 0));
		const date = apartData?.lastUpdateAt && new Date(apartData.lastUpdateAt);
		date.setHours(date.getHours() - 3);
		const daysLag = today.getTime() - date.getTime();
		const day = getFormattedDate(daysLag, date);
		const time = `${date.getHours()}:${(date.getMinutes() < 10 ? '0' : '') + date.getMinutes()}`;
		return `${day}, в ${time} (МСК)`;
	}, [apartData?.lastUpdateAt, getFormattedDate]);

	const isReserved = isPresent === 'Reserved';
	const isLiked = typeMark === 'LIKE';
	const isMortgage = filterGlobal?.selectionMode === 'mortgage';
	const isNew = !checkIsResaleById(id);

	useEffect(() => {
		setDislikeOpen(dislikeMenuOpen);
	}, [dislikeMenuOpen]);

	const changeApartOpinion = useCallback(
		async (opinion) => {
			await changeApartOpinionCommon(opinion, id)(store, context, dispatch);

			if (opinion === 'DISLIKE' && typeof apartData?.selectionInfo?.availableNoticeList === 'object') {
				dispatch(setDislikeMenuOpen(true));
			}
		},
		[store, context, dispatch, id],
	);

	const handleDislikeClose = useCallback(async () => {
		await changeApartOpinionCommon('DISLIKE', id)(store, context, dispatch);
		dispatch(setDislikeMenuOpen(false));
	}, [store, context, dispatch, id]);

	const getPriceLabel = useCallback(() => {
		if (isMortgage && mortgage?.isLoaded && mortgage?.suitablePrograms?.length > 0) {
			return formatValuesNumber(mortgage.suitablePrograms[0].monthlyPayment, 'rubMonth');
		}
		if (isMortgage && mortgageInfo?.monthlyPayment) {
			return formatValuesNumber(mortgageInfo.monthlyPayment, 'rubMonth');
		}
		if (priceTotalMin) {
			return formatValuesNumber(priceTotalMin, 'rub');
		}
		return 'По запросу';
	}, [isMortgage, mortgage, mortgageInfo, priceTotalMin]);

	const withNotices = typeMark === 'DISLIKE' && userDislikes?.length !== 0;

	useEffect(() => {
		if (!apartment.livingBanner) {
			getTextsFromDatabase('site_self_cta_block', ['block_text'], LIRA_SET_LIVING_BANNER_TEXTS)(
				dispatch,
				store.getState,
				context,
			);
		}
	}, [apartment.livingBanner, dispatch, store, context]);

	/* Если текущая кватира не находится в подборе, то у нее нет selectionInfo.
	 Нужно проверять это иначе не верно отображается статус при оценке квартиры */
	const isShowTypeMark = isOpenTypeMark && apartData?.selectionInfo?.availableNoticeList;

	return (
		<StickyBarRoot>
			<StickyBlock>
				<StickyBarWrapper isReserved={isReserved}>
					<StatusWrapper isDisplayed>
						<StatusLabels>
							<Label icon={isNew ? isNewApartIcon : houseIconSmall} iconFill={theme.darkGrayColor}>
								{isNew ? 'Новостройка' : 'Вторичное'}
							</Label>
							{isApartHotel && (
								<Tooltip content="Нежилая коммерческая недвижимость">
									<div>
										<Label withTooltip>Апартаменты</Label>
									</div>
								</Tooltip>
							)}
							{isReserved && <Label>Забронировано</Label>}
							{sellerType && sellerType === 'Переуступка' && <Label>По переуступке</Label>}
						</StatusLabels>
						{!isMobile && <CompareShareBtns isMobile={false} apartId={id} />}
					</StatusWrapper>
					<InfoArea>
						<PriceInfo>
							<Price>
								<PriceValue isBlack={!discountSum} noActive={!isActive} data-qa="apart_cta_price">
									{getPriceLabel()}
								</PriceValue>
								{isNew && apartData?.priceHistory?.length > 0 && (
									<PriceDiffHistory
										priceHistory={apartData.priceHistory}
										pricePerMeter={apartData?.pricePerMeter}
										avrPriceMeter={apartData?.areaAveragePricePerMeter}
										maxShow={4}
									/>
								)}
							</Price>
							{!isMortgage && pricePerMeter && (
								<SidebarPricePerMetr data-qa="apart_cta_price_m2">
									{formatValuesNumber(pricePerMeter, 'rubSquare')}
								</SidebarPricePerMetr>
							)}
							{isMortgage && (priceTotalMin || pricePerMeter) && (
								<SidebarNormalPrice>
									{mortgageInfo?.eligible && (
										<SidebarPricePerMetr data-qa="apart_cta_price_full">
											{formatValuesNumber(priceTotalMin, 'rub')}
										</SidebarPricePerMetr>
									)}
									{pricePerMeter && (
										<SidebarPricePerMetr data-qa="apart_cta_price_m2">
											{formatValuesNumber(pricePerMeter, 'rubSquare')}
										</SidebarPricePerMetr>
									)}
								</SidebarNormalPrice>
							)}
							{discountSum && (
								<>
									<Price>
										<PriceTitle>Скидка:</PriceTitle>
										<PriceValue noActive={!isActive} data-qa="apart_cta_price_discount">
											{`-${parseInt(discountSum, 10).toLocaleString('ru')}`}
											&nbsp;&#8381;
										</PriceValue>
									</Price>
									<PriceTotalWrapper>
										<PriceTitle>Итого:</PriceTitle>
										<PriceValue isBlack data-qa="apart_cta_price_total">
											{parseInt(priceDiscount, 10).toLocaleString('ru')}
											&nbsp;&#8381;
										</PriceValue>
									</PriceTotalWrapper>
								</>
							)}
						</PriceInfo>
					</InfoArea>
					{mortgageInfo.loanPeriod && mortgageInfo.loanRate && (
						<SidebarMortgageInfo>
							{mortgageInfo.monthlyPayment && (
								<SidebarMortgageInfoProgram>
									<CalcImg>{CalcIcon}</CalcImg>
									<SidebarMortgageLink onClick={handleScrollToMortgage} $underline>
										В ипотеку от{' '}
										{formatValuesNumber(
											mortgage?.suitablePrograms?.length > 0
												? mortgage.suitablePrograms[0].monthlyPayment
												: mortgageInfo.monthlyPayment,
											'rubMonth',
										)}
									</SidebarMortgageLink>
								</SidebarMortgageInfoProgram>
							)}
						</SidebarMortgageInfo>
					)}

					{dislikeOpen && !withNotices && isActive && !isShowTypeMark && (
						<ButtonWrapper>
							<Button
								size="s"
								style={{ width: '100%' }}
								design="rounded"
								onClick={() =>
									handlerModalToggle(isNew ? 'outerCallbackDeveloper' : 'outerCallbackAdvert')
								}
							>
								Связаться с&nbsp;{isNew ? 'застройщиком' : 'продавцом'}
							</Button>
							{developer && (
								<DeveloperLogo
									data-qa="apart_cta_developer_logo"
									id="linkDeveloper"
									to={`/zastroischiki/${developer?.slug}`}
									target="_blank"
								>
									{developer?.logo ? (
										<img
											src={developer.logo}
											alt={developer.name}
											onError={(e) => {
												e.target.onerror = null;
												e.target.src = emptyLogo;
											}}
										/>
									) : (
										<img src={emptyLogo} alt={developer.name} />
									)}
								</DeveloperLogo>
							)}
						</ButtonWrapper>
					)}

					<RightBlock $withBorder={dislikeOpen && !withNotices && isActive && !isShowTypeMark}>
						{(apartData?.selectionInfo?.rankPlace || apartData?.selectionInfo?.relevancePercent) &&
							dislikeOpen &&
							!withNotices && (
								<RankPlaceRelevance
									rankPlace={apartData.selectionInfo.rankPlace}
									relevancePercent={apartData.selectionInfo.relevancePercent}
									wishesTexts={apartData.selectionInfo.wishesTexts}
									withIcon={false}
								/>
							)}
						{!discountSum && FIRST_DISCOUNT_NAME && (
							<StockRow>
								<StockLabel>Акция:</StockLabel>
								<StockNameWrapper
									data-qa="apart_cta_price_discount_action"
									onClick={handleScrollToDiscounts}
								>
									<StockName>{FIRST_DISCOUNT_NAME}</StockName>
								</StockNameWrapper>
							</StockRow>
						)}

						{typeMark === 'DISLIKE' &&
							dislikeOpen &&
							userDislikes?.length === 0 &&
							apartData?.selectionInfo?.availableNoticeList && (
								<DislikePopupWrapper>
									<PopupSelector
										type="dislike"
										onClose={handleDislikeClose}
										noticeList={apartData?.selectionInfo?.availableNoticeList}
										className="apartPage"
										size="s"
										withCloseBtn
										hide={() => dispatch(setDislikeMenuOpen(false))}
										apartId={id}
									/>
								</DislikePopupWrapper>
							)}

						{!typeMark && isActive && <LikeDislikeBtns withText changeApartOpinion={changeApartOpinion} />}
						{!!typeMark && !dislikeOpen && (
							<LikeDislikeMark
								likeMark={isLiked}
								changeApartOpinion={changeApartOpinion}
								userDislikes={userDislikes}
								withNotices={withNotices}
							/>
						)}
						{!isActive && <SoldMark />}
						{tags && <ApartCardTags oldStyle tags={tags} />}
					</RightBlock>
				</StickyBarWrapper>
				{isActive && livingBanner?.blockText && (
					<LivingBanner>
						<LivingBannerTitle>{livingBanner.blockText?.title}</LivingBannerTitle>
						<LivingBannerText>{livingBanner.blockText?.text}</LivingBannerText>
						<LivingBannerButton>
							<Button
								data-qa="apart_cta_callback_btn"
								id="buttonCallback"
								style={{ padding: '2px 18px', height: '44px', borderColor: 'currentColor' }}
								onClick={() => handlerModalToggle('innerCallback')}
								color="white"
								design="rounded"
								size="xs"
							>
								Обратиться к специалисту
							</Button>
						</LivingBannerButton>
						<LivingBannerIcon>
							<img
								src={bannerIcon}
								srcSet={`${bannerIcon}, ${bannerIcon2x} 2x, ${bannerIcon3x} 3x`}
								alt=""
							/>
						</LivingBannerIcon>
					</LivingBanner>
				)}
				<UpdatedTime>
					<SellerInfo>
						<span data-qa="apart_cta_nmarketid">id&nbsp;{nmarketId || apartData.id}</span>
						&nbsp;
						{!!sellerType && getSellerTypeText()}
					</SellerInfo>

					{lastUpdateAt && (
						<UpdatedText>
							Обновлено&nbsp;
							<Tooltip
								content="Мы обновляем информацию от застройщика дважды в сутки"
								maxWidth="220px"
								placement="bottom"
								className="small"
							>
								<HelpText data-qa="apart_cta_updated">{getUpdateDate()}</HelpText>
							</Tooltip>
						</UpdatedText>
					)}
				</UpdatedTime>
			</StickyBlock>
		</StickyBarRoot>
	);
};

StickyBar.defaultProps = {
	handleScrollToDiscounts: () => {},
};

export default memo(StickyBar);
